// src/services/admin.ts
import api from './api';
import { User, Role, Unit, KategoriMasalah, StandarSPMI, IndikatorSPMI, Periode, PaginatedResponse } from '../types';

// ========================
// USER MANAGEMENT
// ========================
export interface UserCreatePayload {
  name: string;
  email: string;
  password: string;
  role_id: number;
  unit_id?: number;
  phone?: string;
  is_active?: boolean;
}

export interface UserUpdatePayload {
  name?: string;
  email?: string;
  password?: string;
  role_id?: number;
  unit_id?: number;
  phone?: string;
  is_active?: boolean;
}

export interface UserFilter {
  role_id?: number;
  unit_id?: number;
  is_active?: boolean;
  search?: string;
  page?: number;
  per_page?: number;
}

// ========================
// UNIT MANAGEMENT
// ========================
export interface UnitCreatePayload {
  parent_id?: number | null;
  name: string;
  code: string;
  type: 'UNIVERSITAS' | 'FAKULTAS' | 'PRODI' | 'PUSAT' | 'LEMBAGA' | 'BAGIAN' | 'LAB' | 'WAREK';
  leader_id?: number | null;
  secretary_id?: number | null;
  is_active?: boolean;
}

export interface UnitUpdatePayload {
  parent_id?: number | null;
  name?: string;
  code?: string;
  type?: string;
  leader_id?: number | null;
  secretary_id?: number | null;
  is_active?: boolean;
}

// ========================
// SYSTEM SETTINGS
// ========================
export interface SystemSetting {
  key: string;
  value: string;
  type: 'string' | 'number' | 'int' | 'boolean' | 'json';
  group: string;
  description?: string;
}

// ========================
// ACTIVITY LOG
// ========================
export interface ActivityLog {
  id: number;
  log_name: string;
  description: string;
  subject_type: string;
  subject_id: number;
  causer_type: string;
  causer_id: number;
  properties: Record<string, any>;
  created_at: string;
  causer?: User;
}

export interface LogFilter {
  log_name?: string;
  causer_id?: number;
  subject_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface AdminSystemInfo {
  app_version: string;
  laravel_version: string;
  php_version: string;
  db_size: string;
  storage_used: string;
  storage_total_gb?: number | null;
  storage_free_gb?: number | null;
  storage_usage_percent?: number | null;
  total_users: number;
  total_laporan: number;
  uptime: string;
  uptime_available?: boolean;
  uptime_note?: string;
  uptime_source?: string;
  response_time_ms?: number;
  backup_supported?: boolean;
  backup_method?: string;
  backup_note?: string;
  last_backup?: {
    available: boolean;
    filename?: string | null;
    path?: string | null;
    size?: string | null;
    created_at?: string | null;
    age_hours?: number | null;
  };
  health?: {
    status: 'healthy' | 'warning' | 'critical';
    summary: string;
    issue_count?: number;
    healthy_count?: number;
    checked_at?: string;
    checks?: Array<{
      key: string;
      label: string;
      status: 'healthy' | 'warning' | 'critical';
      message: string;
      meta?: string | null;
    }>;
  };
}

export interface AdminSystemBackupResponse {
  success: boolean;
  message: string;
  data?: {
    filename: string;
    path: string;
    size: string;
    method: string;
    tables: number;
    rows: number;
    created_at: string;
  };
}

export const adminService = {
  // ========================
  // USERS
  // ========================
  users: {
    getAll: async (filters?: UserFilter) => {
      const res = await api.get('/admin/users', { params: filters });
      return res.data as PaginatedResponse<User>;
    },

    getById: async (id: number) => {
      const res = await api.get(`/admin/users/${id}`);
      return res.data as User;
    },

    create: async (data: UserCreatePayload) => {
      const res = await api.post('/admin/users', data);
      return res.data as User;
    },

    update: async (id: number, data: UserUpdatePayload) => {
      const res = await api.put(`/admin/users/${id}`, data);
      return res.data as User;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/users/${id}`);
    },

    toggleActive: async (id: number) => {
      const res = await api.post(`/admin/users/${id}/toggle-active`);
      return res.data;
    },

    resetPassword: async (id: number, newPassword: string) => {
      const res = await api.post(`/admin/users/${id}/reset-password`, { password: newPassword });
      return res.data;
    },

    uploadAvatar: async (id: number, file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await api.post(`/admin/users/${id}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },

    import: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/users/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },

    export: async (filters?: UserFilter) => {
      try {
        const res = await api.get('/admin/users/export', {
          params: filters,
          responseType: 'arraybuffer',
        });

        const contentType = res.headers['content-type'];
        if (contentType && contentType.includes('application/json')) {
          const errorData = JSON.parse(new TextDecoder().decode(res.data));
          throw new Error(errorData.message || 'Export failed');
        }

        return res.data;
      } catch (error: any) {
        console.error('Export service error:', error);
        throw error;
      }
    },
  },

  // ========================
  // ROLES
  // ========================
  roles: {
    getAll: async () => {
      const res = await api.get('/admin/roles');
      const data = res.data?.data || res.data;
      return Array.isArray(data) ? data : [];
    },

    getById: async (id: number) => {
      const res = await api.get(`/admin/roles/${id}`);
      return res.data?.data || res.data;
    },

    create: async (data: { name: string; display_name: string; description?: string; level?: number }) => {
      const res = await api.post('/admin/roles', data);
      return res.data?.data || res.data;
    },

    update: async (id: number, data: Partial<{ name: string; display_name: string; description?: string; level?: number }>) => {
      const res = await api.put(`/admin/roles/${id}`, data);
      return res.data?.data || res.data;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/roles/${id}`);
    },

    updateScopes: async (roleId: number, kategoriIds: number[]) => {
      const res = await api.put(`/admin/roles/${roleId}/scopes`, { kategori_ids: kategoriIds });
      return res.data;
    },
  },

  // ========================
  // UNITS
  // ========================
  units: {
    getAll: async () => {
      const res = await api.get('/admin/units');
      return res.data as Unit[];
    },

    getAllFlat: async () => {
      const res = await api.get('/admin/units/all');
      return res.data as Unit[];
    },

    getTree: async () => {
      const res = await api.get('/admin/units/tree');
      return res.data as Unit[];
    },

    getById: async (id: number) => {
      const res = await api.get(`/admin/units/${id}`);
      return res.data as Unit;
    },

    create: async (data: UnitCreatePayload) => {
      const res = await api.post('/admin/units', data);
      return res.data as Unit;
    },

    update: async (id: number, data: UnitUpdatePayload) => {
      const res = await api.put(`/admin/units/${id}`, data);
      return res.data as Unit;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/units/${id}`);
    },

    toggleActive: async (id: number) => {
      const res = await api.post(`/admin/units/${id}/toggle-active`);
      return res.data;
    },
  },

  // ========================
  // KATEGORI MASALAH
  // ========================
  kategori: {
    getAll: async () => {
      const res = await api.get('/admin/kategori');
      return res.data as KategoriMasalah[];
    },

    create: async (data: { nama: string; deskripsi?: string; color: string; icon?: string }) => {
      const res = await api.post('/admin/kategori', data);
      return res.data as KategoriMasalah;
    },

    update: async (id: number, data: Partial<KategoriMasalah>) => {
      const res = await api.put(`/admin/kategori/${id}`, data);
      return res.data as KategoriMasalah;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/kategori/${id}`);
    },
  },

  // ========================
  // STANDAR SPMI
  // ========================
  standar: {
    getAll: async () => {
      const res = await api.get('/admin/standar');
      return res.data as StandarSPMI[];
    },

    create: async (data: { kode: string; nama: string; deskripsi?: string }) => {
      const res = await api.post('/admin/standar', data);
      return res.data as StandarSPMI;
    },

    update: async (id: number, data: Partial<StandarSPMI>) => {
      const res = await api.put(`/admin/standar/${id}`, data);
      return res.data as StandarSPMI;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/standar/${id}`);
    },
  },

  // ========================
  // INDIKATOR SPMI
  // ========================
  indikator: {
    getAll: async (standarId?: number) => {
      const res = await api.get('/admin/indikator', { params: { standar_id: standarId } });
      return res.data as IndikatorSPMI[];
    },

    create: async (data: { standar_spmi_id: number; kode: string; nama: string; deskripsi?: string; target_value?: string }) => {
      const res = await api.post('/admin/indikator', data);
      return res.data as IndikatorSPMI;
    },

    update: async (id: number, data: Partial<IndikatorSPMI>) => {
      const res = await api.put(`/admin/indikator/${id}`, data);
      return res.data as IndikatorSPMI;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/indikator/${id}`);
    },
  },

  // ========================
  // PERIODE
  // ========================
  periode: {
    getAll: async () => {
      const res = await api.get('/admin/periode');
      return res.data as Periode[];
    },

    create: async (data: { nama: string; tahun_ajaran: string; semester: string; tanggal_mulai: string; tanggal_selesai: string; is_active?: boolean }) => {
      const res = await api.post('/admin/periode', data);
      return res.data as Periode;
    },

    update: async (id: number, data: Partial<Periode>) => {
      const res = await api.put(`/admin/periode/${id}`, data);
      return res.data as Periode;
    },

    delete: async (id: number) => {
      await api.delete(`/admin/periode/${id}`);
    },

    setActive: async (id: number) => {
      const res = await api.post(`/admin/periode/${id}/set-active`);
      return res.data;
    },
  },

  // ========================
  // SYSTEM SETTINGS
  // ========================
  settings: {
    getAll: async () => {
      const res = await api.get('/admin/settings');
      return res.data.data as SystemSetting[];
    },

    getByGroup: async (group: string) => {
      const res = await api.get(`/admin/settings/group/${group}`);
      return res.data.data as SystemSetting[];
    },

    get: async (key: string) => {
      const res = await api.get(`/admin/settings/${key}`);
      return res.data.data as SystemSetting;
    },

    update: async (key: string, value: string) => {
      const res = await api.put(`/admin/settings/${key}`, { value });
      return res.data;
    },

    updateBatch: async (settings: { key: string; value: string }[]) => {
      const res = await api.put('/admin/settings/batch', { settings });
      return res.data;
    },
    sendTestEmail: async (email?: string) => {
      const res = await api.post('/admin/settings/test-email', email ? { email } : {});
      return res.data;
    },
    uploadLogo: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post('/admin/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    uploadFavicon: (file: File) => {
      const formData = new FormData();
      formData.append('favicon', file);
      return api.post('/admin/settings/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    uploadLoginBackground: (file: File) => {
      const formData = new FormData();
      formData.append('login_background', file);
      return api.post('/admin/settings/login-background', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    deleteLoginBackground: () => {
      return api.delete('/admin/settings/login-background');
    },
  },

  // ========================
  // ACTIVITY LOGS
  // ========================
  logs: {
    getAll: async (filters?: LogFilter) => {
      const res = await api.get('/admin/logs', { params: filters });
      // Note: Paginated responses might need similar handling depending on usage
      // But usually paginated response wrapper matches the cast if backend structure aligns
      // Let's assume pagination structure is distinct or handled elsewhere for now
      return res.data as PaginatedResponse<ActivityLog>;
    },

    getBySubject: async (type: string, id: number) => {
      const res = await api.get(`/admin/logs/${type}/${id}`);
      return res.data.data as ActivityLog[];
    },

    export: async (filters?: LogFilter) => {
      try {
        const res = await api.get('/admin/logs/export', {
          params: filters,
          responseType: 'arraybuffer',
        });

        const contentType = res.headers['content-type'];
        if (contentType && contentType.includes('application/json')) {
          const errorData = JSON.parse(new TextDecoder().decode(res.data));
          throw new Error(errorData.message || 'Export failed');
        }

        return res.data;
      } catch (error: any) {
        console.error('Export service error:', error);
        throw error;
      }
    },

    cleanup: async (beforeDate: string) => {
      const res = await api.post('/admin/logs/cleanup', { before_date: beforeDate });
      return res.data;
    },
  },

  // ========================
  // SYSTEM
  // ========================
  system: {
    getInfo: async (): Promise<AdminSystemInfo> => {
      const res = await api.get('/admin/system/info');
      const payload = res.data?.data ?? res.data ?? null;

      if (!payload || typeof payload !== 'object') {
        throw new Error('Response system info tidak valid dari server.');
      }

      return payload as AdminSystemInfo;
    },

    clearCache: async () => {
      const res = await api.post('/admin/system/clear-cache');
      return res.data;
    },

    backup: async () => {
      const res = await api.post('/admin/system/backup');
      return res.data as AdminSystemBackupResponse;
    },

    /**
     * Bersihkan semua data laporan (kecuali data master)
     * @param confirmation - Harus "HAPUS SEMUA DATA" untuk konfirmasi
     */
    cleanReports: async (confirmation: string) => {
      const res = await api.post('/admin/system/clean-reports', { confirmation });
      return res.data as { success: boolean; message: string; output?: string };
    },
  },
};
